import useGetInternalWidgetConfig from '@hooks/api/queries/widgets/useGetInternalWidgetConfig';
import useSubscription from '@src/hooks/useSubscription';
import VerticalLine from '@src/sld/components/Line/VerticalLine';
import { DataPointWithValue } from '@src/types/DataPoint';
import { SiteMeta } from '@src/types/SiteMeta';
import { WidgetTypeDeviceList } from '@src/types/Widget';
import storeConnector from '@store/storeConnector';
import { ReactElement } from 'react';
import ArrowDown from '../components/ArrowDown/ArrowDown';
import ArrowUp from '../components/ArrowUp/ArrowUp';
import BreakerBasic from '../components/Breaker/BreakerBasic';
import LableValueRender from '../components/LableValueRender';
import MultiSwitchConnector from '../components/MultiSwitchConnector/MultiSwitchConnector';
import { Resistor } from '../components/Resistor/Resistor';
import { getDataPointCollectionByPointNames } from '../utils';
import { BearCanyon_Device_Points_Config } from './devicePointsConfig';

type BearCanyonSLDProps = {
  siteMeta: SiteMeta;
};

function BearCanyon_Diagram(props: BearCanyonSLDProps): ReactElement {
  const { siteMeta } = props;

  const { data: rtacWidgetConfig } =
    useGetInternalWidgetConfig<WidgetTypeDeviceList>(
      'bear_canyon_sld_rtac',
      {
        sourceDeviceId: siteMeta.siteId,
      },
      { enabled: true },
    );

  const rtacDataPointsWithValue = useSubscription(
    rtacWidgetConfig?.dataPoints || [],
  ).filter((point) => point.sourceDeviceId == 'rtac_1');

  let m1_52DataPoints: DataPointWithValue[] | undefined;
  let f1_52DataPoints: DataPointWithValue[] | undefined;
  let f2_52DataPoints: DataPointWithValue[] | undefined;
  let f3_52DataPoints: DataPointWithValue[] | undefined;

  if (rtacDataPointsWithValue.length > 0) {
    m1_52DataPoints = getDataPointCollectionByPointNames(
      rtacDataPointsWithValue,
      BearCanyon_Device_Points_Config.M1_52_DATA_POINTS.map((point) =>
        point.pointName.toLowerCase(),
      ),
    );

    f1_52DataPoints = getDataPointCollectionByPointNames(
      rtacDataPointsWithValue,
      BearCanyon_Device_Points_Config.F1_52_DATA_POINTS.map((point) =>
        point.pointName.toLowerCase(),
      ),
    );

    f2_52DataPoints = getDataPointCollectionByPointNames(
      rtacDataPointsWithValue,
      BearCanyon_Device_Points_Config.F2_52_DATA_POINTS.map((point) =>
        point.pointName.toLowerCase(),
      ),
    );

    f3_52DataPoints = getDataPointCollectionByPointNames(
      rtacDataPointsWithValue,
      BearCanyon_Device_Points_Config.F3_52_DATA_POINTS.map((point) =>
        point.pointName.toLowerCase(),
      ),
    );
  }

  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      xlinkHref='http://www.w3.org/1999/xlink'
      viewBox='200 40 1200 700'
      xmlSpace='preserve'
      colorInterpolationFilters='sRGB'
      className='st10'
      style={{ fill: 'none', stroke: 'currentColor', overflowX: 'auto' }}
    >
      <title>Bear Canyon</title>
      <text
        x={769}
        y={80}
        fontSize={8}
        fill='white'
        stroke='white'
        strokeWidth={0.2}
      >
        PG&E
      </text>
      <text
        x={767}
        y={90}
        fontSize={8}
        fill='white'
        stroke='white'
        strokeWidth={0.2}
      >
        230KV
      </text>

      {/* up arrow */}
      <ArrowUp xCoordinate={777.5} yCoordinate={100} />

      <VerticalLine
        translateXCoordinate={780}
        translateYCoordinate={100}
        length={70}
        color='white'
        width={0.5}
      />

      {/* ****** resitor-breakers start ****** */}
      <g transform='translate(140 -100)'>
        <g transform='rotate(90 575 840) translate(0 0)'>
          <Resistor label={'XFMR T1'} />
        </g>

        <VerticalLine
          translateXCoordinate={640}
          translateYCoordinate={290}
          length={27}
          color='white'
          width={0.5}
        />

        {/* 52M1 - breaker */}
        <BreakerBasic
          labels={[{ label: '52-M1', xCoordinate: 50, yCoordinate: 10 }]}
          translateXCoordinate={625}
          translateYCoordinate={315}
          dataPointsWithValues={m1_52DataPoints}
        />

        <LableValueRender
          xPosition={740}
          yPosition={280}
          dataPoints={m1_52DataPoints}
          pointNames={BearCanyon_Device_Points_Config.M1_52_DATA_POINTS}
        />

        <VerticalLine
          translateXCoordinate={640}
          translateYCoordinate={342}
          length={75}
          color='white'
          width={0.5}
        />

        <MultiSwitchConnector
          translateXCoordinate={335}
          translateYCoordinate={-128}
          horizontalLength={330}
          verticalLength={50}
          startingXCoordinate={140}
        />

        {/* 52F1 - breaker */}
        <BreakerBasic
          labels={[{ label: '52-F1', xCoordinate: 35, yCoordinate: 15 }]}
          translateXCoordinate={460}
          translateYCoordinate={468}
          dataPointsWithValues={f1_52DataPoints}
        />

        <VerticalLine
          translateXCoordinate={475}
          translateYCoordinate={495}
          length={100}
          color='white'
          width={0.5}
        />

        <ArrowDown xCoordinate={472.5} yCoordinate={595} />

        <text
          x={420}
          y={620}
          fontSize={8}
          fill='white'
          stroke='white'
          strokeWidth={0.2}
        >
          34.5KV BESS FEEDER #1
        </text>

        {/* 52F2 - breaker */}
        <VerticalLine
          translateXCoordinate={640}
          translateYCoordinate={418}
          length={50}
          color='white'
          width={0.5}
        />

        <BreakerBasic
          labels={[{ label: '52-F2', xCoordinate: 35, yCoordinate: 15 }]}
          translateXCoordinate={625}
          translateYCoordinate={468}
          dataPointsWithValues={f2_52DataPoints}
        />

        <VerticalLine
          translateXCoordinate={640}
          translateYCoordinate={496}
          length={100}
          color='white'
          width={0.5}
        />

        <ArrowDown xCoordinate={637.5} yCoordinate={595} />

        <text
          x={590}
          y={620}
          fontSize={8}
          fill='white'
          stroke='white'
          strokeWidth={0.2}
        >
          34.5KV BESS FEEDER #2
        </text>

        {/* 52F3 - breaker */}
        <BreakerBasic
          labels={[{ label: '52-F3', xCoordinate: 35, yCoordinate: 15 }]}
          translateXCoordinate={790}
          translateYCoordinate={468}
          dataPointsWithValues={f3_52DataPoints}
        />

        <VerticalLine
          translateXCoordinate={805}
          translateYCoordinate={496}
          length={100}
          color='white'
          width={0.5}
        />

        <ArrowDown xCoordinate={803} yCoordinate={595} />

        <text
          x={760}
          y={620}
          fontSize={8}
          fill='white'
          stroke='white'
          strokeWidth={0.2}
        >
          34.5KV BESS FEEDER #3
        </text>
      </g>
    </svg>
  );
}

export default storeConnector(BearCanyon_Diagram, {
  config: ['siteMeta'],
});
